import type { CollectionName, RemoteRecord, SubscribeEvent } from './types';

// The single boundary the rest of the app uses to talk to the sync
// backend. The Pocketbase implementation is in pocketbaseClient.ts; the
// in-memory test double is in inMemoryClient.ts. Tests inject either
// via setSyncClient(...) below.

export interface SyncClient {
  // Authenticate using the sync code (used as both username and password
  // on the `families` auth collection). Resolves with the family id;
  // throws SyncAuthError on a wrong code.
  authWithSyncCode(code: string): Promise<{ familyId: string }>;

  // Forget the persisted JWT. After this call isAuthed() === false.
  authClear(): void;

  isAuthed(): boolean;
  currentFamilyId(): string | null;

  // Read every record matching `filter` (Pocketbase filter syntax). The
  // engine uses this for boot-time and post-reconnect catch-up.
  getFullList<T extends RemoteRecord>(c: CollectionName, filter?: string): Promise<T[]>;

  // Idempotent write: tries update(id), falls back to create on 404.
  // Resolves with the server's view of the row (notably its `updated`).
  upsert<T extends RemoteRecord>(
    c: CollectionName,
    record: Partial<T> & { id: string },
  ): Promise<T>;

  delete(c: CollectionName, id: string): Promise<void>;

  // Real-time subscription. Returns an unsubscribe function. The filter
  // string follows Pocketbase semantics; pass '' to watch everything.
  subscribe(c: CollectionName, filter: string, cb: (e: SubscribeEvent) => void): () => void;

  // Register a callback that fires when the realtime connection is
  // re-established after a disconnect. Used by the engine to trigger
  // catch-up recovery — PB SSE does not replay missed events.
  // Returns an unsubscribe function. Optional — test doubles may omit.
  onReconnect?(cb: () => void): () => void;
}

// Module-level singleton with dependency injection for tests. The
// production binding is set lazily by getSyncClient() the first time the
// app actually needs to talk to the backend (so unit tests that never
// call getSyncClient() don't pull in the Pocketbase SDK).
let injected: SyncClient | null = null;
let cachedDefault: SyncClient | null = null;

export function setSyncClient(client: SyncClient | null): void {
  injected = client;
  // Discard the cached default so a future getSyncClient() rebuilds it
  // when the test re-injects null (happens between test files).
  cachedDefault = null;
}

export async function getSyncClient(): Promise<SyncClient> {
  if (injected) return injected;
  if (cachedDefault) return cachedDefault;
  const { createPocketbaseClient } = await import('./pocketbaseClient');
  cachedDefault = createPocketbaseClient();
  return cachedDefault;
}
